import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Crown, LogOut, Loader2, Trash2, Clock, BellRing, Baby, Plus, Lock, ListMusic, Play, Pause, SkipForward, Square } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { updateProfile, fetchSongs, fetchPosts, deletePost, addChild, removeChild, fetchPlaylists, deletePlaylist, resolveImage } from "@/lib/api";
import PlaylistsPanel from "@/components/PlaylistsPanel";

const AVATAR_COLORS = ["#FFC107", "#0284C7", "#16A34A", "#9333EA", "#FF6B6B", "#1E293B"];
const CHILD_COLORS = ["#FFC107", "#0284C7", "#16A34A", "#9333EA", "#FF6B6B"];
const AGE_BANDS = ["0-2", "2-4", "5-7", "8-10"];

const PRIVACY_OPTIONS = [
  { key: "private_account", label: "Private account", desc: "Only approved followers see your family profile" },
  { key: "hide_photos", label: "Hide family photos", desc: "Your photos stay out of public discovery" },
  { key: "comments_enabled", label: "Allow comments", desc: "Let other parents comment on your posts" },
];

const NOTIF_OPTIONS = [
  { key: "new_song", label: "New songs & stories", desc: "When fresh content lands in the library" },
  { key: "reactions", label: "Reactions", desc: "When someone reacts to your posts" },
  { key: "replies", label: "Replies", desc: "When someone comments on your posts" },
  { key: "announcements", label: "Announcements", desc: "Important updates from the team" },
];

const Toggle = ({ on, onToggle, testid }) => (
  <button
    onClick={onToggle}
    data-testid={testid}
    role="switch"
    aria-checked={on}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-[#16A34A]" : "bg-slate-200"}`}
  >
    <motion.span
      animate={{ x: on ? 22 : 2 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="absolute top-1 h-5 w-5 rounded-full bg-white shadow"
    />
  </button>
);

const Profile = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ name: "", family_name: "", avatar_color: "" });
  const [childDraft, setChildDraft] = useState({ name: "", age_band: "2-4", color: "#FFC107" });

  useEffect(() => {
    if (user === false) navigate("/signin");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.user_id) {
      setDraft({ name: user.name, family_name: user.family_name || "", avatar_color: user.avatar_color });
      fetchSongs().then(setSongs).catch(() => {});
      fetchPosts()
        .then((ps) => setMyPosts(ps.filter((p) => p.author_user_id === user.user_id)))
        .catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#FFFDF6]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFC107]" />
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      setUser(await updateProfile(draft));
    } finally {
      setSaving(false);
    }
  };

  const togglePrivacy = async (key) => {
    setUser(await updateProfile({ [key]: !user.privacy?.[key] }));
  };

  const toggleNotif = async (key) => {
    setUser(await updateProfile({ [`notif_${key}`]: !user.notif_prefs?.[key] }));
  };

  const removePost = async (postId) => {
    await deletePost(postId);
    setMyPosts((ps) => ps.filter((p) => p.post_id !== postId));
  };

  const submitChild = async (e) => {
    e.preventDefault();
    if (!childDraft.name.trim()) return;
    const children = await addChild(childDraft);
    setUser({ ...user, children });
    setChildDraft({ name: "", age_band: "2-4", color: "#FFC107" });
  };

  const deleteChild = async (childId) => {
    const children = await removeChild(childId);
    setUser({ ...user, children });
  };

  const favourites = songs.filter((s) => (user.favourites || []).includes(s.song_id));
  const children = user.children || [];

  return (
    <div className="min-h-screen bg-[#FFFDF6] pb-24 pt-28" data-testid="profile-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link to="/" className="text-sm font-bold text-slate-400 transition-colors hover:text-slate-700" data-testid="profile-back-link">
          &larr; Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.12)]"
        >
          <div className="flex flex-wrap items-center gap-5">
            <span
              className="grid h-20 w-20 place-items-center rounded-full font-display text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: user.avatar_color }}
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-full w-full rounded-full object-cover" />
              ) : (
                user.name[0]
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="profile-name">{user.name}</h1>
                {user.plan === "family_plus" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-[#FFC107]" data-testid="plan-badge">
                    <Crown className="h-3 w-3" /> FAMILY+
                  </span>
                ) : (
                  <span className="rounded-full bg-[#F8F5EC] px-3 py-1 text-xs font-bold text-slate-500" data-testid="plan-badge">
                    Free plan
                  </span>
                )}
              </div>
              <p className="mt-1 font-hand text-xl text-slate-500">{user.family_name}</p>
              <p className="text-sm text-slate-400">{user.email}</p>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="profile-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2DDD0] px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-[#FF6B6B] hover:text-[#FF6B6B]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>

          <div className="mt-8 grid gap-4 border-t border-[#F8F5EC] pt-8 sm:grid-cols-3">
            <label className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Display name</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                data-testid="profile-name-input"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Family name</span>
              <input
                value={draft.family_name}
                onChange={(e) => setDraft((d) => ({ ...d, family_name: e.target.value }))}
                data-testid="profile-family-input"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              />
            </label>
            <div className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Avatar colour</span>
              <div className="flex items-center gap-2" data-testid="profile-color-picker">
                {AVATAR_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setDraft((d) => ({ ...d, avatar_color: c }))}
                    data-testid={`profile-color-${c.replace("#", "")}`}
                    aria-label={`Avatar colour ${c}`}
                    className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      draft.avatar_color === c ? "border-slate-900 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="profile-save-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 font-display text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="mt-6 rounded-3xl border border-[#E2DDD0] bg-white p-8"
          data-testid="children-panel"
        >
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <Baby className="h-5 w-5 text-[#0284C7]" /> Child profiles
          </h2>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
            <Lock className="h-3.5 w-3.5 text-[#16A34A]" />
            Always private — only you can see them, and they control the kids' view.
          </p>
          {children.length > 0 && (
            <ul className="mt-5 space-y-3">
              {children.map((c) => (
                <li key={c.child_id} className="flex items-center gap-3 rounded-2xl bg-[#F8F5EC] p-4" data-testid={`child-${c.child_id}`}>
                  <span className="grid h-10 w-10 place-items-center rounded-full font-display text-base font-bold text-white" style={{ backgroundColor: c.color }}>
                    {c.name[0]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800">{c.name}</p>
                    <p className="text-xs font-bold text-slate-400">Age {c.age_band}</p>
                  </div>
                  <Link
                    to={`/kids?child=${c.child_id}`}
                    data-testid={`child-open-${c.child_id}`}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
                  >
                    Open {c.name}'s view
                  </Link>
                  <button
                    onClick={() => deleteChild(c.child_id)}
                    data-testid={`child-delete-${c.child_id}`}
                    aria-label={`Remove ${c.name}`}
                    className="grid h-8 w-8 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={submitChild} className="mt-5 flex flex-wrap items-end gap-3" data-testid="add-child-form">
            <label className="grid flex-1 gap-1.5 min-w-36">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">First name</span>
              <input
                value={childDraft.name}
                onChange={(e) => setChildDraft((d) => ({ ...d, name: e.target.value }))}
                data-testid="child-name-input"
                placeholder="Sophia"
                maxLength={40}
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Age</span>
              <select
                value={childDraft.age_band}
                onChange={(e) => setChildDraft((d) => ({ ...d, age_band: e.target.value }))}
                data-testid="child-age-select"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              >
                {AGE_BANDS.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </label>
            <div className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Colour</span>
              <div className="flex items-center gap-1.5">
                {CHILD_COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setChildDraft((d) => ({ ...d, color: c }))}
                    data-testid={`child-color-${c.replace("#", "")}`}
                    aria-label={`Colour ${c}`}
                    className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                      childDraft.color === c ? "border-slate-900 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <button
              type="submit"
              data-testid="add-child-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0284C7] px-5 py-3 font-display text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6 rounded-3xl border border-[#E2DDD0] bg-white p-8"
          data-testid="privacy-panel"
        >
          <h2 className="font-display text-xl font-bold text-slate-900">Privacy controls</h2>
          <div className="mt-5 space-y-4">
            {PRIVACY_OPTIONS.map((o) => (
              <div key={o.key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-800">{o.label}</p>
                  <p className="text-sm text-slate-500">{o.desc}</p>
                </div>
                <Toggle
                  on={!!user.privacy?.[o.key]}
                  onToggle={() => togglePrivacy(o.key)}
                  testid={`privacy-toggle-${o.key.replace(/_/g, "-")}`}
                />
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="mt-6 rounded-3xl border border-[#E2DDD0] bg-white p-8"
          data-testid="notif-prefs-panel"
        >
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <BellRing className="h-5 w-5 text-[#9333EA]" /> Notification preferences
          </h2>
          <div className="mt-5 space-y-4">
            {NOTIF_OPTIONS.map((o) => (
              <div key={o.key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-800">{o.label}</p>
                  <p className="text-sm text-slate-500">{o.desc}</p>
                </div>
                <Toggle
                  on={user.notif_prefs?.[o.key] !== false}
                  onToggle={() => toggleNotif(o.key)}
                  testid={`notif-toggle-${o.key.replace(/_/g, "-")}`}
                />
              </div>
            ))}
          </div>
        </motion.div>

        <PlaylistsPanel songs={songs} />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="rounded-3xl border border-[#E2DDD0] bg-white p-8"
            data-testid="favourites-panel"
          >
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
              <Star className="h-5 w-5 fill-[#FFC107] text-[#FFC107]" /> Saved favourites
            </h2>
            {favourites.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">
                No favourites yet — tap the heart on any song to save it here.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {favourites.map((s) => (
                  <li key={s.song_id} className="flex items-center gap-3" data-testid={`favourite-${s.song_id}`}>
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="font-semibold text-slate-800">{s.title}</span>
                    <span className="ml-auto text-xs font-bold text-slate-400">{s.duration}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className="rounded-3xl border border-[#E2DDD0] bg-white p-8"
            data-testid="my-posts-panel"
          >
            <h2 className="font-display text-xl font-bold text-slate-900">My posts</h2>
            {myPosts.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">
                Nothing shared yet — your posts appear here, including ones under review.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {myPosts.map((p) => (
                  <li key={p.post_id} className="rounded-2xl bg-[#F8F5EC] p-4" data-testid={`my-post-${p.post_id}`}>
                    <p className="line-clamp-2 text-sm text-slate-700">{p.text}</p>
                    <div className="mt-2 flex items-center justify-between">
                      {p.status === "pending" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B45309]">
                          <Clock className="h-3 w-3" /> Under review
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-[#16A34A]">Live</span>
                      )}
                      <button
                        onClick={() => removePost(p.post_id)}
                        data-testid={`delete-post-${p.post_id}`}
                        aria-label="Delete post"
                        className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>

        {user.plan !== "family_plus" && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-slate-900 p-8 text-white"
          >
            <div>
              <h2 className="flex items-center gap-2 font-display text-xl font-bold">
                <Crown className="h-5 w-5 text-[#FFC107]" /> Unlock the whole universe
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Full library, offline listening, printables and bedtime collections.
              </p>
            </div>
            <Link
              to="/#shop"
              data-testid="profile-upgrade-btn"
              className="rounded-full bg-[#FFC107] px-6 py-3 font-display text-sm font-semibold text-slate-900 transition-transform hover:scale-105"
            >
              See what's coming
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Profile;
